import React, { useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, doc, updateDoc, addDoc, getDoc, setDoc } from 'firebase/firestore';
import { ROLES, useUserRole, hasRole } from '../lib/auth/roles';
import { useSchoolId } from '../lib/auth/schoolContext';
import { getStaffDisplayName } from '../lib/staff';
import { AREA_OF_NEED_LABELS } from '../lib/apdrSchemas';
import {
  SCHOOL_WEEKS_PER_YEAR,
  annualCostFromWeekly,
  computeStudentProvisionCosts,
  frequencyToSessions,
  parseDurationToMinutes,
} from '../features/ehcna/lib/provisionCost';
import html2pdf from 'html2pdf.js';

// Displays per-student provision + interventions, staff assignments, and cost summary
export default function ProvisionMap({ studentId, refreshKey = 0 }) {
  const { role } = useUserRole();
  const canEdit = hasRole(role, [ROLES.SENCO]);
  const { schoolId } = useSchoolId();

  const [interventions, setInterventions] = useState([]);
  const [studentProvisions, setStudentProvisions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const pdfRef = useRef();

  const loadProvisionData = async () => {
    if (!canEdit || !schoolId || !studentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [intSnap, asnSnap, provSnap, staffSnap] = await Promise.all([
      getDocs(query(
        collection(db, 'interventions'),
        where('schoolId', '==', schoolId),
        where('students', 'array-contains', studentId)
      )),
      getDocs(query(
        collection(db, 'interventionAssignments'),
        where('schoolId', '==', schoolId),
        where('studentId', '==', studentId)
      )),
      getDocs(query(
        collection(db, 'studentProvisions'),
        where('schoolId', '==', schoolId),
        where('studentId', '==', studentId),
        where('status', '==', 'active')
      )),
      getDocs(query(
        collection(db, 'staff'),
        where('schoolId', '==', schoolId),
        where('active', '==', true)
      )),
    ]);

    setInterventions(intSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setAssignments(asnSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setStudentProvisions(
      provSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    );
    setStaff(staffSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => {
    if (!canEdit) {
      setLoading(false);
      return;
    }
    loadProvisionData().catch((e) => {
      console.warn('Failed to load costed provision map', e);
      setLoading(false);
    });
  }, [studentId, canEdit, schoolId, refreshKey]);

  const sessionsFromSchedule = (schedule) => {
    if (!Array.isArray(schedule) || schedule.length === 0) return null;
    const ds = schedule
      .map((s) => { const d = new Date(s); return isNaN(d.getTime()) ? null : d; })
      .filter(Boolean)
      .sort((a,b) => a.getTime() - b.getTime());
    if (ds.length === 0) return null;
    const first = ds[0].getTime();
    const last = ds[ds.length - 1].getTime();
    const days = Math.max(1, Math.round((last - first) / (1000*60*60*24)) + 1);
    const weeksSpan = Math.max(1, Math.round(days / 7));
    return schedule.length / weeksSpan;
  };

  const parseDurationToMinutes = (duration) => {
    // Accept formats like "30", "30 minutes", "45 min", "1h", "1 h 30"
    if (!duration) return 30;
    const s = String(duration).toLowerCase();
    // If contains 'h', parse hours and minutes
    const hMatch = s.match(/(\d+)\s*h/);
    const mMatch = s.match(/(\d+)\s*m/);
    if (hMatch || mMatch) {
      const hours = hMatch ? parseInt(hMatch[1], 10) : 0;
      const mins = mMatch ? parseInt(mMatch[1], 10) : 0;
      const total = (isNaN(hours) ? 0 : hours) * 60 + (isNaN(mins) ? 0 : mins);
      return total || 30;
    }
    // Fallback: first number in string as minutes
    const firstNum = s.match(/(\d+)/);
    return firstNum ? parseInt(firstNum[1], 10) : 30;
  };

  // Ensure assignments exist with sensible defaults from the intervention group
  useEffect(() => {
    if (!canEdit) return;
    if (!interventions.length) return;
    // Prefer to run after staff list is available to avoid zero-cost snapshots
    (async () => {
      // For each intervention for this student, create an assignment if missing
      const missing = interventions.filter(i => !assignments.find(a => a.interventionId === i.id && a.studentId === studentId));
      if (!missing.length) return;
      // If we expect to use lead staff for cost, ensure staff list is ready first
      const needsStaff = missing.some(i => !!i.leadStaffId);
      if (needsStaff && staff.length === 0) return; // wait until staff is loaded
      for (const i of missing) {
        try {
          // Use lead staff if available; sessions/minutes from frequency & duration
          const updates = {
            staffId: i.leadStaffId || null,
            sessionsPerWeek: (sessionsFromSchedule(i.sessionSchedule) || frequencyToSessions(i.frequency)),
            minutesPerSession: parseDurationToMinutes(i.duration),
          };
          await upsertAssignment(i.id, updates);
        } catch (e) {
          // Continue with next; do not block others
          // eslint-disable-next-line no-console
          console.warn('Failed to initialize assignment from intervention defaults', i?.id, e);
        }
      }
    })();
  }, [interventions, assignments, canEdit, studentId, staff]);

  // Ensure existing assignments are populated from intervention defaults if fields are missing
  const [backfilledIds, setBackfilledIds] = useState(new Set());
  useEffect(() => {
    if (!canEdit) return;
    if (!interventions.length) return;
    (async () => {
      for (const i of interventions) {
        const a = assignments.find(x => x.interventionId === i.id && x.studentId === studentId);
        if (!a) continue;
        const needsStaff = !a.staffId && i.leadStaffId;
        const needsSessions = !(a.sessionsPerWeek > 0) && (i.frequency || (i.sessionSchedule && i.sessionSchedule.length));
        const needsMinutes = !(a.minutesPerSession > 0) && i.duration;
        if (!(needsStaff || needsSessions || needsMinutes)) continue;
        const updates = {};
        if (needsStaff) updates.staffId = i.leadStaffId;
        if (needsSessions) updates.sessionsPerWeek = (sessionsFromSchedule(i.sessionSchedule) || frequencyToSessions(i.frequency));
        if (needsMinutes) updates.minutesPerSession = parseDurationToMinutes(i.duration);
        try {
          await upsertAssignment(i.id, updates);
          setBackfilledIds((prev) => new Set(prev).add(i.id));
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('Failed to fill missing assignment fields', i?.id, e);
        }
      }
    })();
  }, [interventions, assignments, canEdit]);

  // Repair any zero-cost snapshots once staff list is available
  useEffect(() => {
    if (!canEdit) return;
    if (staff.length === 0) return;
    const zeroCost = assignments.filter(a => a.staffId && (a.weeklyCost === 0 || a.weeklyCost === undefined));
    if (zeroCost.length === 0) return;
    (async () => {
      for (const a of zeroCost) {
        try {
          await upsertAssignment(a.interventionId, {}); // recompute snapshot with current staff rates
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('Failed to repair zero-cost snapshot', a?.id, e);
        }
      }
    })();
  }, [assignments, staff, canEdit]);

  const byIntervention = useMemo(() => {
    const map = {};
    assignments.forEach(a => { map[a.interventionId] = a; });
    return map;
  }, [assignments]);

  const computeWeeklyCost = (assignmentLike) => {
    if (!assignmentLike || !assignmentLike.staffId) return 0;
    const staffMember = staff.find(s => s.id === assignmentLike.staffId);
    if (!staffMember) return 0;
    const hourly = Number(staffMember?.hourlyRate || 0);
    const mins = Number(assignmentLike.minutesPerSession || 0);
    const sessions = Number(assignmentLike.sessionsPerWeek || 0);
    return (hourly / 60) * mins * sessions;
  };

  const getProvisionWeekly = (provision) => {
    if (provision.weeklyCost !== undefined && Number(provision.weeklyCost) > 0) {
      return Number(provision.weeklyCost);
    }
    const staffMember = provision.staffId ? staff.find((s) => s.id === provision.staffId) : null;
    return computeStudentProvisionCosts(provision, staffMember).weeklyCost;
  };

  const getProvisionSessionsPerWeek = (provision) =>
    frequencyToSessions(provision.frequency);

  const getProvisionMinutesPerSession = (provision) =>
    parseDurationToMinutes(provision.duration);

  // Sum interventions + active student provision rows
  const interventionWeeklyTotal = useMemo(() => {
    return interventions.reduce((sum, i) => {
      const a = byIntervention[i.id];
      const weekly = (a && a.weeklyCost !== undefined && Number(a.weeklyCost) > 0)
        ? Number(a.weeklyCost)
        : computeWeeklyCost(a || {});
      return sum + weekly;
    }, 0);
  }, [interventions, byIntervention, staff]);

  const provisionWeeklyTotal = useMemo(() => {
    return studentProvisions.reduce((sum, p) => sum + getProvisionWeekly(p), 0);
  }, [studentProvisions, staff]);

  const totalWeeklyCost = interventionWeeklyTotal + provisionWeeklyTotal;

  const upsertAssignment = async (interventionId, updates) => {
    const existing = assignments.find(a => a.interventionId === interventionId && a.studentId === studentId);
    // Build effective fields for cost calc: prefer updates then existing then defaults
    const effective = {
      staffId: updates.staffId !== undefined ? updates.staffId : (existing?.staffId ?? null),
      sessionsPerWeek: updates.sessionsPerWeek !== undefined ? updates.sessionsPerWeek : (existing?.sessionsPerWeek ?? 0),
      minutesPerSession: updates.minutesPerSession !== undefined ? updates.minutesPerSession : (existing?.minutesPerSession ?? 0),
    };
    let staffMember = effective.staffId ? staff.find(s => s.id === effective.staffId) : null;
    // Fallback: fetch staff doc if not already loaded locally
    if (!staffMember && effective.staffId) {
      try {
        const snap = await getDoc(doc(db, 'staff', effective.staffId));
        if (snap.exists()) staffMember = { id: snap.id, ...snap.data() };
      } catch { /* noop */ }
    }
    const hourlyAtCalc = Number(staffMember?.hourlyRate || 0);
    const weeklyCost = ((hourlyAtCalc / 60) * Number(effective.minutesPerSession || 0) * Number(effective.sessionsPerWeek || 0)) || 0;

    const payload = { 
      ...updates, 
      // Persist snapshot values so totals are stable across report runs
      weeklyCost,
      hourlyRateAtCalc: hourlyAtCalc,
      calcTimestamp: new Date(),
      updatedAt: new Date(),
    };

    if (existing) {
      await updateDoc(doc(db, 'interventionAssignments', existing.id), payload);
    } else {
      await addDoc(collection(db, 'interventionAssignments'), { studentId, interventionId, status: 'active', createdAt: new Date(), schoolId, ...payload });
    }
    // Mirror non-sensitive schedule to public collection (include weeklyCost snapshot)
    const scheduleId = `${studentId}_${interventionId}`;
    const sched = {
      studentId,
      interventionId,
      staffId: effective.staffId ?? null,
      sessionsPerWeek: Number(effective.sessionsPerWeek || 0),
      minutesPerSession: Number(effective.minutesPerSession || 0),
      weeklyCost,
      status: 'active',
      updatedAt: new Date(),
    };
    await setDoc(doc(db, 'interventionSchedules', scheduleId), { ...sched, schoolId }, { merge: true });
    // Refresh local state
    const asnSnap = await getDocs(query(
      collection(db, 'interventionAssignments'),
      where('schoolId', '==', schoolId),
      where('studentId', '==', studentId)
    ));
    setAssignments(asnSnap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const exportCSV = () => {
    const headers = ['Type', 'Provision', 'Staff', 'Sessions/Week', 'Minutes/Session', 'Hourly (£)', 'Weekly Cost (£)'];
    const provisionRows = studentProvisions.map((p) => {
      const staffMember = p.staffId ? staff.find((s) => s.id === p.staffId) : null;
      const hourly = staffMember ? Number(staffMember.hourlyRate || 0) : 0;
      const weekly = getProvisionWeekly(p);
      const staffLabel = staffMember
        ? getStaffDisplayName(staffMember)
        : (p.staffRoleLabel || '-');
      return [
        'Student provision',
        p.name || '-',
        staffLabel,
        getProvisionSessionsPerWeek(p),
        getProvisionMinutesPerSession(p),
        hourly.toFixed(2),
        weekly.toFixed(2),
      ];
    });
    const interventionRows = interventions.map((i) => {
      const a = byIntervention[i.id];
      const staffMember = a ? staff.find(s => s.id === a.staffId) : null;
      const hourly = (a && a.hourlyRateAtCalc !== undefined && Number(a.hourlyRateAtCalc) > 0)
        ? Number(a.hourlyRateAtCalc)
        : (staffMember ? Number(staffMember?.hourlyRate || 0) : 0);
      const weekly = (a && a.weeklyCost !== undefined && Number(a.weeklyCost) > 0)
        ? Number(a.weeklyCost)
        : (a ? computeWeeklyCost(a) : 0);
      return [
        'Intervention',
        i.name || '-',
        staffMember ? getStaffDisplayName(staffMember) : '-',
        a?.sessionsPerWeek ?? '-',
        a?.minutesPerSession ?? '-',
        hourly.toFixed(2),
        weekly.toFixed(2),
      ];
    });
    const rows = [...provisionRows, ...interventionRows];
    const total = ['', '', '', '', '', 'Total', totalWeeklyCost.toFixed(2)];
    const csv = [headers, ...rows, total].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `provision_${studentId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!pdfRef.current) return;
    const opt = {
      margin: 0.5,
      filename: `provision_${studentId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(pdfRef.current).set(opt).save();
  };

  if (!canEdit) return <div className="p-4 text-gray-600">Only SENCOs can view costing and staff assignments.</div>;
  if (loading) return <div className="p-4 text-gray-500">Loading provision map...</div>;

  return (
    <div ref={pdfRef} className="bg-white rounded shadow">
      <div className="p-4 border-b flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Costed Provision Map</h3>
          <p className="text-sm text-gray-600">
            Includes student provision from above and pull-out interventions. Edit provision in Student provision; edit interventions below.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCSV} className="px-3 py-1.5 border rounded">Export CSV</button>
          <button onClick={exportPDF} className="px-3 py-1.5 border rounded">Export PDF</button>
        </div>
      </div>
      <div className="divide-y">
        {studentProvisions.length === 0 && interventions.length === 0 ? (
          <div className="p-4 text-gray-500">
            No costed provision yet. Add student provision above or assign this pupil to an intervention group.
          </div>
        ) : null}

        {studentProvisions.length > 0 && (
          <div className="px-4 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Student provision (in-class, pastoral, equipment)
          </div>
        )}
        {studentProvisions.map((p) => {
          const staffMember = p.staffId ? staff.find((s) => s.id === p.staffId) : null;
          const weekly = getProvisionWeekly(p);
          const sessions = getProvisionSessionsPerWeek(p);
          const minutes = getProvisionMinutesPerSession(p);
          const areaLabel = AREA_OF_NEED_LABELS[p.areaOfNeed] || p.areaOfNeed || '';
          return (
            <div key={`prov-${p.id}`} className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-center bg-slate-50/50">
              <div className="md:col-span-2">
                <div className="font-medium">{p.name}</div>
                <div className="text-sm text-gray-600">
                  {areaLabel}
                  {p.frequency ? ` • ${p.frequency}` : ''}
                  {p.duration ? ` • ${p.duration}` : ''}
                </div>
                <div className="mt-1 text-xs text-indigo-600">
                  Edit in Student provision above
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500">Staff</label>
                <div className="text-sm p-2">
                  {staffMember
                    ? `${getStaffDisplayName(staffMember)} (£${Number(staffMember.hourlyRate || 0).toFixed(2)}/hr)`
                    : (p.staffRoleLabel || 'Unassigned')}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500">Sessions/Week</label>
                <div className="text-sm p-2">{sessions}</div>
              </div>
              <div>
                <label className="block text-xs text-gray-500">Minutes/Session</label>
                <div className="text-sm p-2">{minutes}</div>
              </div>
              <div>
                <label className="block text-xs text-gray-500">Weekly Cost (£)</label>
                <div className="font-medium">£{weekly.toFixed(2)}</div>
              </div>
            </div>
          );
        })}

        {interventions.length > 0 && (
          <div className="px-4 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Pull-out interventions
          </div>
        )}
        {interventions.map((i) => {
          const a = byIntervention[i.id] || {};
          const weekly = (a && a.weeklyCost !== undefined && Number(a.weeklyCost) > 0)
            ? Number(a.weeklyCost)
            : computeWeeklyCost(a);
          return (
            <div key={i.id} className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
              <div className="md:col-span-2">
                <div className="font-medium">{i.name}</div>
                <div className="text-sm text-gray-600">{i.type || ''}</div>
                {backfilledIds.has(i.id) && (
                  <div className="mt-1 text-xs text-amber-600">
                    Auto-filled from intervention defaults. Adjust at <a href="/interventions" className="underline">Interventions</a> if needed.
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-500">Staff</label>
                <select disabled={!canEdit} className="w-full border rounded p-2" value={a.staffId || ''} onChange={(e)=> upsertAssignment(i.id, { staffId: e.target.value || null })}>
                  <option value="">Unassigned</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{getStaffDisplayName(s)} (£{Number(s.hourlyRate||0).toFixed(2)}/hr)</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500">Sessions/Week</label>
                <input disabled={!canEdit} type="number" min="0" className="w-full border rounded p-2" value={a.sessionsPerWeek ?? ''} onChange={(e)=> upsertAssignment(i.id, { sessionsPerWeek: Number(e.target.value) || 0 })} />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Minutes/Session</label>
                <input disabled={!canEdit} type="number" min="0" className="w-full border rounded p-2" value={a.minutesPerSession ?? ''} onChange={(e)=> upsertAssignment(i.id, { minutesPerSession: Number(e.target.value) || 0 })} />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Weekly Cost (£)</label>
                <div className="font-medium">£{weekly.toFixed(2)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-4 border-t flex flex-wrap items-center justify-end gap-4">
        {studentProvisions.length > 0 && (
          <div className="text-sm text-gray-600">
            Student provision: £{provisionWeeklyTotal.toFixed(2)}/week
          </div>
        )}
        {interventions.length > 0 && (
          <div className="text-sm text-gray-600">
            Interventions: £{interventionWeeklyTotal.toFixed(2)}/week
          </div>
        )}
        <div className="text-sm text-gray-700 font-medium">
          Total weekly: £{totalWeeklyCost.toFixed(2)}/week
          {totalWeeklyCost > 0 && (
            <span className="text-gray-500 font-normal">
              {' '}
              (est. £{annualCostFromWeekly(totalWeeklyCost).toFixed(2)}/year, {SCHOOL_WEEKS_PER_YEAR} school weeks)
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
